import {
  getDefaultPreviewClipDurationsInFrames,
  ImageTransitionBuilder,
  ImageTransitionBuilderProps,
  ImageTransitionWrapperProps,
} from "./ImageTransitionBuilder.ts";
import {
  easeInOutCubic,
  easeInCubic,
  easeOutCubic,
} from "../../utils/easing.ts";
import { msToFrames } from "../../utils/timing.ts";
import { TransitionKind, TransitionSpeed } from "@repo/api/transition";
import { linearTiming } from "@remotion/transitions";
import { fade } from "@remotion/transitions/fade";
import { interpolate } from "remotion";

const transitionKind = TransitionKind.enum.ZOOM_IN_OUT;

export const ImageZoomInOutTransitionBuilder: ImageTransitionBuilder<
  Record<string, unknown>
> = {
  formConfig: {
    kind: transitionKind,
    buildDefaultValue: () => ({ kind: transitionKind, speed: "NORMAL" }),
    speed: ["SLOW", "NORMAL", "FAST"],
  },

  transition: {
    getDurationInFrames: getDurationInFrames,
    buildPresentation: () => fade(),
    buildTiming: (props) =>
      linearTiming({
        durationInFrames: getDurationInFrames(props),
        easing: easeInOutCubic,
      }),
  },
  wrappers: {
    nextImageWrapper: {
      buildWrapperStyle: buildNextImageWrapperStyle,
    },

    prevImageWrapper: {
      buildWrapperStyle: buildPrevImageWrapperStyle,
    },
  },

  getPreviewClipDurationsInFrames: getDefaultPreviewClipDurationsInFrames,
};

const TRANSITION_DURATIONS = {
  SLOW: 200,
  NORMAL: 150,
  FAST: 100,
};

export function getDurationInFrames(props: ImageTransitionBuilderProps) {
  if (!props.imageTransition.speed) {
    return 0;
  }

  return msToFrames(
    TRANSITION_DURATIONS[props.imageTransition.speed],
    props.fps,
  );
}

const NEXT_IMAGE_DURATIONS = {
  SLOW: 2000,
  NORMAL: 1000,
  FAST: 500,
};

const PREV_IMAGE_DURATIONS = {
  SLOW: 750,
  NORMAL: 500,
  FAST: 250,
};

function getWrapperDurationInFrames(
  speed: TransitionSpeed | undefined,
  speedMapping: Record<TransitionSpeed, number>,
  fps: number,
) {
  if (!speed) {
    return null;
  }

  return msToFrames(speedMapping[speed], fps);
}

function buildNextImageWrapperStyle({
  fps,
  currentFrame,
  imageTransition,
}: ImageTransitionWrapperProps) {
  const endFrame = getWrapperDurationInFrames(
    imageTransition.speed,
    NEXT_IMAGE_DURATIONS,
    fps,
  );

  if (!endFrame) {
    return null;
  }

  const scale = interpolate(currentFrame, [0, endFrame], [1.5, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeOutCubic,
  });

  if (currentFrame > endFrame) {
    return null;
  }

  return {
    transform: `scale(${scale})`,
  };
}

function buildPrevImageWrapperStyle({
  fps,
  imageDurationInFrames,
  currentFrame,
  imageTransition,
}: ImageTransitionWrapperProps) {
  const transitionDurationFrames = getWrapperDurationInFrames(
    imageTransition.speed,
    PREV_IMAGE_DURATIONS,
    fps,
  );

  if (!transitionDurationFrames) {
    return null;
  }

  const endFrame = Math.max(1, imageDurationInFrames - 1);
  const startFrame = Math.max(0, endFrame - transitionDurationFrames);
  const scale = interpolate(currentFrame, [startFrame, endFrame], [1, 1.5], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInCubic,
  });

  if (currentFrame < startFrame) {
    return null;
  }

  return {
    transform: `scale(${scale})`,
  };
}
